import React, { useEffect, useState } from "react";
import getIcon from "../../../../utils/Icon";
import { Link2Icon, ChevronDown } from "lucide-react";
import { message, Tooltip } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
    addTask,
    addMultipleTasks,
    clearTasks,
    removeTask,
} from "../../../../store/slices/taskSlice";
import tasksService from "../../../../api/taskApi";
import { DeleteOutlined } from "@ant-design/icons";

export default function PromoTasks({create}) {
    const [openBlocks, setOpenBlocks] = useState({ default: true, social: true });
    const [hasChanges, setHasChanges] = useState(false);
    const [taskType, setTaskType] = useState("");
    const [taskLink, setTaskLink] = useState("");

    const dispatch = useDispatch();
    const tasks = useSelector((state) => state.tasks.tasks);
    const allTasks = useSelector((state) => state.tasks);
    const promoForm = useSelector((state) => state.promoForm);

    const taskTypes = [
        { value: "twitter", label: "Twitter" },
        { value: "telegram", label: "Telegram" },
        { value: "youtube_sub", label: "YouTube Подписка" },
        { value: "youtube_video", label: "YouTube Видео" },
        { value: "youtube_bell", label: "Колокольчик" },
        { value: "instagram", label: "Instagram" },
        { value: "custom", label: "Ссылка" },
    ];

    const getTasks = async () => {
        try {
            const tasks = await tasksService.getTaskById(promoForm.id);
            dispatch(clearTasks());
            dispatch(addMultipleTasks(tasks));
            setHasChanges(false);
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    const handleRemoveTask = (details) => {
        dispatch(removeTask(details));
        setHasChanges(true);
    };

    const handleAddTask = () => {
        if (!taskType || !taskLink) return;

        const exists = tasks.some((task) => task.details === taskLink);
        if (exists) {
            message.warning("Такая ссылка уже добавлена");
            return;
        }

        dispatch(
            addTask({
                title: taskType.replace("_", " "),
                details: taskLink,
            })
        );

        setTaskType("");
        setTaskLink("");
        setHasChanges(true);
    };

    const uploadTasks = async () => {
        try {
            const response = await tasksService.createTaskById(promoForm.id, allTasks);
            message.success("✅ Tasks updated successfully");
            setHasChanges(false);
        } catch (e) {
            console.error(e);
            message.error("❌ Failed to update tasks");
        }
    };

    useEffect(() => {
        getTasks();
    }, []);

    const toggleBlock = (key) => {
        setOpenBlocks((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const taskItem = ({ icon, type, text, link }) => (
        <div key={link || text} className="flex flex-row gap-3 border-b border-white/10 p-2 pb-1">
            <div className="w-10 h-10 relative">
                <img src={icon} className="w-9 h-9 rounded-full" />
                {type && <img src={type} className="w-3 h-3 absolute bottom-1 right-0 rounded-full" />}
            </div>

            <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-col justify-center w-full pr-2">
                    <div className="text-white">{text}</div>
                    {link && (
                        <Tooltip title={link}>
                            <div className="max-w-[200px] truncate text-emerald-400/90">{link}</div>
                        </Tooltip>
                    )}
                </div>
                <div className="flex flex-col h-full items-center justify-center gap-1">
                    <DeleteOutlined
                        onClick={() => handleRemoveTask(link)}
                        className="cursor-pointer text-red-400"
                    />
                    {link && (
                        <Link2Icon
                            className="cursor-pointer text-white/70 transition hover:text-emerald-300"
                            onClick={() => window.open(link, "_blank")}
                        />
                    )}
                </div>
            </div>
        </div>
    );

    const AccordionBlock = ({ title, id, children }) => (
        <div className="flex w-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
            <div
                className="flex cursor-pointer items-center justify-between border-b border-white/10 px-2 py-2"
                onClick={() => toggleBlock(id)}
            >
                <div className="text-[12px] font-thin uppercase tracking-wide text-white/50">{title}</div>
                <motion.div animate={{ rotate: openBlocks[id] ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown className="w-4 h-4 text-white" />
                </motion.div>
            </div>

            <AnimatePresence initial={false}>
                {openBlocks[id] && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    return (
        <div className="relative h-full min-h-0 w-full flex-1 space-y-4 overflow-y-auto pb-[160px] pt-0">
            {hasChanges && (
                <div className="sticky top-0 z-10 rounded-xl border border-emerald-400/25 bg-[#070A12]/90 py-2 backdrop-blur-md">
                    <button
                        onClick={uploadTasks}
                        className="w-full rounded-lg border border-emerald-400/40 bg-emerald-500/20 py-2 text-xs font-bold text-emerald-100 transition hover:bg-emerald-500/30"
                    >
                        ✅ Confirm Changes
                    </button>
                </div>
            )}

            <AccordionBlock title="default" id="default">
                {taskItem({
                    icon: getIcon("wallet"),
                    type: "",
                    text: "Connect your wallet",
                    link: "",
                })}
                {taskItem({
                    icon: getIcon("octo"),
                    type: getIcon("twitter"),
                    text: "Follow OctopusSQD",
                    link: "https://x.com/?lang=en",
                })}
                {taskItem({
                    icon: getIcon("octo"),
                    type: getIcon("telegram"),
                    text: "Join OctopusSQD channel",
                    link: "https://telegram.org/",
                })}
            </AccordionBlock>

            <AccordionBlock title="social media" id="social">
                {tasks.map((task) =>
                    taskItem({
                        icon: getIcon("octo"),
                        type: getIcon(task.title),
                        text: task.title,
                        link: task.details,
                    })
                )}
            </AccordionBlock>
            {
                !create&&<div className="absolute bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-[#070A12]/95 px-4 py-3 backdrop-blur-xl">
                    <div className="mb-2 flex flex-wrap gap-2">
                        {taskTypes.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => setTaskType(type.value)}
                                className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${
                                    taskType === type.value
                                        ? "border-sky-400/45 bg-sky-500/20 text-sky-100"
                                        : "border-white/10 bg-white/[0.06] text-white/65 hover:border-white/20 hover:bg-white/10"
                                }`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            value={taskLink}
                            onChange={(e) => setTaskLink(e.target.value)}
                            placeholder="Вставьте ссылку"
                            className="flex-1 rounded-lg border border-white/10 bg-white/[0.06] px-3 py-1 text-xs text-white/90 outline-none placeholder:text-white/35 focus:border-emerald-400/40"
                        />
                        <button
                            onClick={handleAddTask}
                            className="rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-3 py-1 text-xs font-medium text-emerald-100 transition hover:bg-emerald-500/30"
                        >
                            ➕ Добавить
                        </button>
                    </div>
                </div>
            }

        </div>
    );
}
